/**
 * Good to Know — the A5 coverage zone (UX-13 S3 extraction from
 * OrganizationDetail): the editable grid for direct-write roles
 * (A5CoverageSection) and the read-only checklist for everyone else
 * (GoodToKnowChecklist). Markup preserved; the Chip variants adopt the S0
 * semantic names (memory; facts read neutral) — with the Editable*
 * renderers now deriving from the Chip primitive's exported map, the
 * edit-mode chips finally render the same brand identity set as the
 * read-only chips (the drifted inline mirrors are deleted).
 */

import { type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { EditableSelect, type EditableSelectProps } from '@/components/EditableSelect';
import { EditableBoolean, type EditableBooleanProps } from '@/components/EditableBoolean';
import { EditableMultiSelect, type EditableMultiSelectProps } from '@/components/EditableMultiSelect';
import { Section } from '@/components/peace-hub-port/ResourceDetail';
import type { ChipVariant } from '@/components/primitives/Chip';
import {
  MEDICAID_VALUES,
  SERVICE_DELIVERY_MODELS,
  PAYMENT_TYPES,
  GENDER_RESTRICTION_VALUES,
  asOptions,
} from '@/lib/facetTaxonomy';
import type { ProviderDetail } from '@/lib/types';

// Phase 1.9.4 — A5 vocabulary option lists for the inline-edit primitives.
// {value,label} options derived from the shared facet taxonomy (single
// source of truth: @/lib/facetTaxonomy). The directory filters + the suggest
// form read the same lists, so the edit page can't drift from them.
const MEDICAID_OPTIONS = asOptions(MEDICAID_VALUES);
const SERVICE_DELIVERY_MODEL_OPTIONS = asOptions(SERVICE_DELIVERY_MODELS);
const PAYMENT_TYPE_OPTIONS = asOptions(PAYMENT_TYPES);
const GENDER_RESTRICTION_OPTIONS = asOptions(GENDER_RESTRICTION_VALUES);

/**
 * Medicaid → Chip variant mapping (Phase 1.9.3 introduced; Phase 1.9.4
 * shared between read-only Chip rendering + EditableSelect's variantForValue;
 * UX-13 S2/S3 — the semantic names).
 */
export function medicaidChipVariant(value: string): ChipVariant {
  if (value === 'Yes · Straight') return 'affirm';
  if (value === 'No') return 'neutral';
  return 'caution';
}

export interface A5CoverageSectionProps {
  provider: ProviderDetail;
  canEdit: boolean;
  onSaveMedicaid: EditableSelectProps<string>['onSave'];
  onSaveDementiaUnit: EditableBooleanProps['onSave'];
  onSavePaymentTypes: EditableMultiSelectProps<string>['onSave'];
  onSaveServiceDeliveryModels: EditableMultiSelectProps<string>['onSave'];
  onSaveGenderRestrictions: EditableSelectProps<string>['onSave'];
}

export function A5CoverageSection({
  provider,
  canEdit,
  onSaveMedicaid,
  onSaveDementiaUnit,
  onSavePaymentTypes,
  onSaveServiceDeliveryModels,
  onSaveGenderRestrictions,
}: A5CoverageSectionProps): ReactNode {
  return (
    <Section title="Good to know">
      {/* Care details group (§3 — 11px uppercase sage-400 group label). */}
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-sage-400">
        Care details
      </p>
      {/* Top row: feature-class primitives. */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <EditableBoolean
          value={provider.dementia_unit}
          onSave={onSaveDementiaUnit}
          label="Memory Care"
          trueLabel="Secure Dementia Unit"
          trueVariant="memory"
          trueIcon="🔒"
          canEdit={canEdit}
        />
        {/* Phase 1.10 — Delivery method evolves to plural via EditableMultiSelect
            (Path A wire-shape evolution per r2 disposition #11). The 39
            multi-value rows from Phase 1.9.2's audit table now surface
            their full N-element vocabulary inline. */}
        <EditableMultiSelect
          values={provider.service_delivery_models}
          options={SERVICE_DELIVERY_MODEL_OPTIONS}
          onSave={onSaveServiceDeliveryModels}
          label="Delivery method"
          variant="neutral"
          canEdit={canEdit}
        />
        <EditableSelect
          value={provider.gender_restrictions}
          options={GENDER_RESTRICTION_OPTIONS}
          onSave={onSaveGenderRestrictions}
          label="Gender Restrictions"
          variant="neutral"
          canEdit={canEdit}
        />
      </div>

      {/* Payment Types sub-block: Medicaid + payment_types primitives. */}
      <div className="mt-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-sage-400 mb-1.5">
          Payment types
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          <EditableSelect
            value={provider.medicaid}
            options={MEDICAID_OPTIONS}
            onSave={onSaveMedicaid}
            label="Medicaid"
            variantForValue={medicaidChipVariant}
            canEdit={canEdit}
          />
          <EditableMultiSelect
            values={provider.payment_types}
            options={PAYMENT_TYPE_OPTIONS}
            onSave={onSavePaymentTypes}
            label="Payment type"
            variant="neutral"
            canEdit={canEdit}
          />
        </div>
      </div>
    </Section>
  );
}

export interface GoodToKnowChecklistProps {
  provider: ProviderDetail;
}

export function GoodToKnowChecklist({ provider }: GoodToKnowChecklistProps): ReactNode {
  const items: string[] = [];
  if (provider.dementia_unit === true) items.push('Secure Dementia Unit (Memory Care)');
  if (provider.medicaid && provider.medicaid !== 'No') items.push(`Accepts Medicaid (${provider.medicaid})`);
  // E3 (Option 1) — service_delivery_models carries location + home-care-type
  // (hourly/24-7/live-in) + 'Respite'; surface 'Respite' as "Offers respite".
  for (const m of provider.service_delivery_models ?? []) {
    items.push(m === 'Respite' ? 'Offers respite' : m);
  }
  for (const p of provider.payment_types ?? []) items.push(`${p} accepted`);
  if (provider.gender_restrictions) items.push(provider.gender_restrictions);

  return (
    <Section title="Good to know">
      {items.length === 0 ? (
        <p className="text-sm text-ink-soft">Once someone adds coverage details, you'll see them here.</p>
      ) : (
        <div className="grid gap-x-4 gap-y-2.5 sm:grid-cols-2">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 text-sm text-ink">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-tight bg-tick-affirm text-sage-800">
                <Check size={13} strokeWidth={2.4} aria-hidden="true" />
              </span>
              {item}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}
